import {monthNumberToLabelI18N, round, UUIDfromList} from "@/libs/Helpers";
import constants from '@/libs/Constants';

/**
 * Simulation inputs
 */
export class SimulationData {
    constructor(month, precipitationTemperatureValue, precipitationQuantile, temperatureQuantile, snowStateQuantile, soilWaterQuantile) {
        this.month = month;
        this.precipitationTemperatureValue = precipitationTemperatureValue;
        this.precipitationQuantile = precipitationQuantile;
        this.temperatureQuantile = temperatureQuantile;
        this.snowStateQuantile = snowStateQuantile;
        this.soilWaterQuantile = soilWaterQuantile;
    }

    monthLabel(i18nObject, style = 'long') {
        return i18nObject.d(
            new Date(
                1,
                this.month - 1,
                1
            ),
            {'month': style});
    }

    get uuid() {
        return UUIDfromList([
            this.month,
            this.precipitationTemperatureValue,
            this.precipitationQuantile,
            this.temperatureQuantile,
            this.snowStateQuantile,
            this.soilWaterQuantile,
        ]);
    }

    get monthIsSummer() {
        return this.month >= 6 && this.month <= 9;
    }

    get serialized() {
        let precipitationTemperatureType = '';
        let precipitationTemperatureValue = null;

        if (this.precipitationQuantile !== null) {
            precipitationTemperatureType = 'precipitation';
            precipitationTemperatureValue = this.precipitationQuantile;
        } else {
            precipitationTemperatureType = 'temperature';
            precipitationTemperatureValue = this.temperatureQuantile;
        }

        return {
            month: this.month,
            precipitation_temperature_type: precipitationTemperatureType,
            precipitation_temperature_percentile: precipitationTemperatureValue,
            snow_water_percentile: this.snowStateQuantile,
            soil_water_percentile: this.soilWaterQuantile,
        }
    }
}

export class SimulationVariable {
    constructor(simulation_id, variable_id, month) {
        this.simulation_id = simulation_id;
        this.variable_id = variable_id;
        this.month = month;
    }

    monthLabel(i18nObject, style = 'long') {
        return this.month !== null ?
            monthNumberToLabelI18N(this.month, i18nObject, style) :
            '';
    }
}

/**
 * Anthropic works
 */
export class SimulationAnthropicWorksAbstract {
    setDefaultValues() {
        Object.keys(this.default).forEach(key => {
            this[key] = this.default[key];
        })
    }

    get hasDefaultValues() {
        return Object.keys(this.default).every(key => {
            return this[key] === this.default[key];
        });
    }

    constructor(id, name, type, defaultValues, editable = true) {
        if (this.constructor === SimulationAnthropicWorksAbstract) {
            throw new Error("Class is of abstract type and can't be instantiated");
        }
        this.id = id;
        this.name = name;
        this.type = type;
        this.default = defaultValues;
        this.editable = editable;

        this.setDefaultValues();
    }

    compareEqual(other) {
        return false;
    }

    get serialized() {
        return null;
    }
}

export class SimulationAnthropicWorksDamLake extends SimulationAnthropicWorksAbstract {
    constructor(id, name, editable = true) {
        super(
            id,
            name,
            constants.LAYERS.DAM_LAKE,
            {
                dischargeTurbineFlow: 1.0
            },
            editable
        );
    }


    clone() {
        let newObject = new SimulationAnthropicWorksDamLake(this.id, this.name);
        newObject.dischargeTurbineFlow = this.dischargeTurbineFlow;
        return newObject;
    }

    compareEqual(other) {
        return other instanceof SimulationAnthropicWorksDamLake ?
            this.dischargeTurbineFlow === other.dischargeTurbineFlow :
            true;
    }

    get uuid() {
        return UUIDfromList([
            this.id,
            this.type,
            this.dischargeTurbineFlow
        ]);
    }

    get serialized() {
        return {
            id: this.id,
            discharge_turbine_flow: this.dischargeTurbineFlow
        }
    }
}

export class SimulationAnthropicWorksDerivation extends SimulationAnthropicWorksAbstract {
    constructor(id, name, editable = true) {
        super(
            id,
            name,
            constants.LAYERS.DERIVATION,
            {
                dischargeDerivation: true
            },
            editable
        );
    }

    clone() {
        let newObject = new SimulationAnthropicWorksDerivation(this.id, this.name);
        newObject.dischargeDerivation = this.dischargeDerivation;
        return newObject;
    }

    compareEqual(other) {
        return other instanceof SimulationAnthropicWorksDerivation ?
            this.dischargeDerivation === other.dischargeDerivation :
            true;
    }

    get uuid() {
        return UUIDfromList([
            this.id,
            this.type,
            this.dischargeDerivation
        ]);
    }

    get serialized() {
        return {
            id: this.id,
            open: this.dischargeDerivation
        }
    }
}

export class SimulationAnthropicWorksHydroPlant extends SimulationAnthropicWorksAbstract {
    constructor(id, name, editable = true) {
        super(
            id,
            name,
            constants.LAYERS.PLANT,
            {
                dischargeTurbineMaximum: 1.0
            },
            editable
        );
    }

    clone() {
        let newObject = new SimulationAnthropicWorksHydroPlant(this.id, this.name);
        newObject.dischargeTurbineMaximum = this.dischargeTurbineMaximum;
        return newObject;
    }

    compareEqual(other) {
        return other instanceof SimulationAnthropicWorksHydroPlant ?
            this.dischargeTurbineMaximum === other.dischargeTurbineMaximum :
            true;
    }

    get uuid() {
        return UUIDfromList([
            this.id,
            this.type,
            this.dischargeTurbineMaximum
        ]);
    }
}